import PropTypes from 'prop-types';
import React from 'react';

import { DEFAULT_LAST_SHOW, HISTORY_OPTIONS, LAST_SHOW_OPTIONS, SLIDERS, STATUS_OPTIONS } from '../constants';
import CheckboxFacet from './CheckboxFacet';
import KeyedFacet from './KeyedFacet';
import RadioFacet from './RadioFacet';
import RangeSliderRow from './RangeSliderRow';
import SearchableCheckboxFacet from './SearchableCheckboxFacet';
import Section from './Section';

const toEntries = (counts = {}) => Object.entries(counts).map(([label, count]) => ({ label, count }));

const toggleValue = (list, value) =>
  (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

// Smart suggestions only applies q/status/genre/agency/avgCap/spotify server-side
// (see useSmartSuggestions#buildParams) — hide the facets it can't act on rather
// than let a booker set one and see nothing change.
const FacetRail = ({ filters, facets, setFilter, facetOptionsUrl, view = 'all' }) => {
  const suggestionsView = view === 'suggestions';
  const sliders = suggestionsView ? SLIDERS.filter((s) => s.key !== 'facebook') : SLIDERS;

  return (
    <aside className="ab-rail">
      <SearchableCheckboxFacet
        title="Genre"
        facetKey="genre"
        facetOptionsUrl={facetOptionsUrl}
        options={toEntries(facets.genre)}
        selected={filters.genre}
        onToggle={(label) => setFilter({ genre: toggleValue(filters.genre, label) })}
        onClear={() => setFilter({ genre: [] })}
        showCounts={!suggestionsView}
      />
      <SearchableCheckboxFacet
        title="Agency"
        facetKey="agency"
        facetOptionsUrl={facetOptionsUrl}
        options={toEntries(facets.agency)}
        selected={filters.agency}
        onToggle={(label) => setFilter({ agency: toggleValue(filters.agency, label) })}
        onClear={() => setFilter({ agency: [] })}
        showCounts={!suggestionsView}
      />
      {!suggestionsView && (
        <KeyedFacet
          title="History"
          options={HISTORY_OPTIONS}
          counts={facets.history}
          selected={filters.history}
          onToggle={(key) => setFilter({ history: toggleValue(filters.history, key) })}
        />
      )}
      <KeyedFacet
        title="Status"
        options={STATUS_OPTIONS}
        counts={facets.status}
        selected={filters.status}
        onToggle={(key) => setFilter({ status: toggleValue(filters.status, key) })}
        showCounts={!suggestionsView}
      />
      {!suggestionsView && (
        <RadioFacet
          title="Last performed"
          options={LAST_SHOW_OPTIONS}
          counts={facets.last_show}
          selected={filters.lastShow}
          noFilterKey={DEFAULT_LAST_SHOW}
          onSelect={(key) => setFilter({ lastShow: key })}
        />
      )}

      <Section title="Draw & reach">
        <div className="ab-sliders">
          {sliders.map((s) => (
            <RangeSliderRow
              key={s.key}
              slider={s}
              value={filters[s.key] || [s.min, s.max]}
              // Treat a full-range selection as "no filter" so nudging a slider to its
              // ends doesn't drop artists with no metric (the range filter is an EXISTS).
              onCommit={(next) => setFilter({ [s.key]: next[0] <= s.min && next[1] >= s.max ? null : next })}
            />
          ))}
        </div>
      </Section>

      {!suggestionsView && (toEntries(facets.tag).length > 0 || filters.tag.length > 0) && (
        <CheckboxFacet
          title="Tags"
          options={toEntries(facets.tag)}
          selected={filters.tag}
          onToggle={(label) => setFilter({ tag: toggleValue(filters.tag, label) })}
          onClear={() => setFilter({ tag: [] })}
        />
      )}

      {!suggestionsView && (
        <Section title="Data quality">
          <button
            type="button"
            className={`ab-toggle ${filters.hasMetrics ? 'ab-toggle--on' : ''}`}
            onClick={() => setFilter({ hasMetrics: !filters.hasMetrics })}
          >
            <span>Hide profiles missing metrics</span>
            <span className="ab-switch"><span className="ab-switch__knob" /></span>
          </button>
        </Section>
      )}
    </aside>
  );
};
FacetRail.propTypes = {
  filters: PropTypes.object.isRequired,
  facets: PropTypes.object.isRequired,
  setFilter: PropTypes.func.isRequired,
  facetOptionsUrl: PropTypes.string.isRequired,
  view: PropTypes.string,
};

export default FacetRail;
